import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Stack, Typography, Chip, IconButton } from '@mui/material'
import { Link, useNavigate } from 'react-router-dom'
import EastIcon from '@mui/icons-material/East'
import { useIsMobile } from '@/hooks/Responsive'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'
import { userSend } from 'api/users'

// Chip 카테고리 표시
const getDisplayCategory = (row) => {
  const noticeYn = String(row.noticeYn ?? '').trim()
  const upendYn = String(row.upendYn ?? '').trim()
  // console.log('@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@')
  // console.log(row)
  // console.log(upendYn);
  // console.log(noticeYn);

  if (upendYn === 'Y') return '전체'
    
  if (noticeYn === 'Y') return '공지'
  
  return '일반'
}

// Chip 색상
const getChipColor = (category) => {
  if (category === '전체') return 'primary'
  if (category === '공지') return 'secondary'
  if (category === '일반') return 'grey'
  return 'grey'
}

// 정렬 우선순위
const categoryOrder = {
  전체: 1,
  공지: 2,
  일반: 3,
}

// 날짜 포맷(2025-11-07 -> 2025.11.07) / 이미 점이면 그대로
const formatDate = (v) => {
  const s = String(v ?? '').trim()
  if (!s) return ''
  if (s.includes('.')) return s
  return s.replaceAll('-', '.')
}

// API row -> 화면 row 매핑
function mapNoticeRow(row) {
  return {
    // NoticeView에서 상세 조회용으로 bbsId를 쓰고 있어서 반드시 포함
    bbsId: row?.bbsId ?? row?.vlabNoticeId ?? row?.id ?? null,

    id: row?.vlabNoticeId ?? row?.id ?? row?.bbsId ?? row?.rowNum,
    vlab: row?.vlab ?? row?.vlabNoticeDivision ?? row?.vlabYn ?? row?.vlabNoticeVlabYn,
    no: row?.rowNum ?? '',
    upendYn: row?.upendYn ?? '',
    noticeYn: row?.noticeYn ?? '',
    title: row?.bbsTitle ?? row?.vlabNoticeTitle ?? row?.title ?? '',
    createdAt: formatDate(row?.registDttm ?? row?.vlabNoticeWriteDt ?? row?.createdAt ?? row?.regDt ?? ''),
    attachments: row?.attachments ?? row?.vlabNoticeFileList ?? row?.files ?? [],
    content: row?.bbsSubstatnce ?? '데이터 없어서 임시로 표시',
  }
}

export default function NoticeSection() {
  const { isMobile } = useIsMobile()
  const lang = useLang()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)

  // 리스트용 페이지 파라미터
  const [page, setPage] = useState(1)
  const pageSize = 5
  const recordSize = 10

  const [confirmedSearch, setConfirmedSearch] = useState('')

  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)

  const fetchList = useCallback(async () => {
    setLoading(true)
    try {
      const params = {
        page,
        pageSize,
        recordSize,
        searchNm: confirmedSearch,
      }

      const res = await userSend('/alarm/getNoticeList', params)

      const list = res?.data?.content ?? []
      const mapped = Array.isArray(list) ? list.map(mapNoticeRow) : []

      setItems(mapped)
      setTotal(Number(res?.data?.totalElements ?? 0))
    } catch (e) {
      console.error('공지사항 조회 실패', e)
      setItems([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, recordSize, confirmedSearch])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  const sortedNotices = useMemo(() => {
    const arr = Array.isArray(items) ? [...items] : []
    arr.sort((a, b) => {
      const A = categoryOrder[getDisplayCategory(a)] ?? 99
      const B = categoryOrder[getDisplayCategory(b)] ?? 99
      if (A !== B) return A - B

      const an = Number(a.no ?? 0)
      const bn = Number(b.no ?? 0)
      return bn - an
    })
    return arr
  }, [items])

  const handleRowClick = useCallback(
    (row) => {
      const searchYn = 'N'
      const navParamData = {
        page,
        pageSize,
        recordSize,
        searchNm: confirmedSearch,
      }

      // 여기서 lang prefix 포함 경로로 이동해야 404 안 남
      navigate(buildLangHref('/alarm/noticeview', lang), {
        state: {
          searchYn,
          navParamData,
          rowData: row,
        },
      })
    },
    [navigate, lang, page, pageSize, recordSize, confirmedSearch]
  )

  return (
    <Box
      sx={{
        mx: 'auto',
        width: '100%',
        maxWidth: '1440px',
        px: 2,
      }}
    >
      {/* 대제목 */}
      <Stack
        direction={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'flex-start' : 'flex-end'}
        justifyContent={isMobile ? 'flex-start' : 'space-between'}
        spacing={2}
        sx={{ maxWidth: '1400px', mx: 'auto', mb: 7 }}
      >
        <Box>
          <Typography component="h3" variant={isMobile ? 'body1' : 'subtitle3'} fontWeight={700} sx={{ color: 'primary.main', mb: 2 }}>
            공지사항
          </Typography>

          <Typography component="p" fontWeight={600} variant={isMobile ? 'h6' : 'h4'}>
            OpenVLab 공지사항
          </Typography>
        </Box>
        <Stack
          component={Link}
          to={buildLangHref('/alarm/notice', lang)}
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            pb: 1.5,
            textDecoration: 'none',
            color: 'inherit',
            cursor: 'pointer',
          }}
        >
          <Typography fontWeight={600}>
            자세히 보기
          </Typography>
          <Box
            sx={{
              width: isMobile ? '32px' : '40px',
              height: isMobile ? '32px' : '40px',
              border: '1px solid',
              borderColor: 'grey.dark',
              color: 'grey.dark',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <EastIcon
              className="arrow-icon"
              sx={{
                fontSize: isMobile ? '16px' : '20px',
              }}
            />
          </Box>
        </Stack>
      </Stack>

      {/* 리스트 */}
      <Stack spacing={3}>
        {sortedNotices.map((row) => {
          const displayCategory = getDisplayCategory(row)
          const chipColor = getChipColor(displayCategory)

          return (
            <Stack
              key={row.id}
              direction={isMobile ? 'column' : 'row'}
              alignItems={isMobile ? 'flex-start' : 'center'}
              justifyContent={isMobile ? 'flex-start' : 'space-between'}
              spacing={isMobile ? 2 : 7}
              sx={{
                px: isMobile ? 3 : 6,
                py: isMobile ? 3 : 4,
                borderRadius: 4,
                bgcolor: displayCategory === '전체' ? 'primary.50' : 'grey.50',
              }}
            >
              <Chip label={displayCategory} variant="contained" color={chipColor} sx={{ height: '32px' }} />

              <Box
                role="button"
                tabIndex={0}
                onClick={() => handleRowClick(row)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleRowClick(row)
                  }
                }}
                sx={{
                  cursor: 'pointer',
                  color: 'text.primary',
                  textDecoration: 'none',
                  flex: 1,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                <Typography
                  variant={isMobile ? 'subtitle3' : 'subtitle2'}
                  fontWeight={500}
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: isMobile ? 2 : 1,
                    WebkitBoxOrient: 'vertical',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {row.title}
                </Typography>

                <Typography
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: isMobile ? 2 : 1,
                    WebkitBoxOrient: 'vertical',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    mt: 1.5,
                  }}
                >
                  {row.content}
                </Typography>
              </Box>

              <Typography color="text.secondary" sx={{ ml: 'auto', pt: 0.5, mb: 'auto !important' }}>
                {row.createdAt}
              </Typography>
            </Stack>
          )
        })}

        {!loading && sortedNotices.length === 0 && (
          <Typography sx={{ color: 'text.secondary', px: 1 }}>공지사항이 없습니다.</Typography>
        )}

        {loading && <Typography sx={{ color: 'text.secondary', px: 1 }}>불러오는 중...</Typography>}
      </Stack>
    </Box>
  )
}
